import React, { useContext, useEffect, useMemo, useState } from "react";
import { ActivityIndicator, Image, Pressable, ScrollView, StyleSheet, View } from "react-native";
import EncryptedStorage from "react-native-encrypted-storage";
import Icon from "react-native-vector-icons/Ionicons";
import { FindifyDataContext, FindifyDataContextType } from "@/contexts/FindifyDataContext";
import { FindifyMenuContext } from "@/contexts/FindifyMenuContext";
import { BasePage } from "../../genericPages/helpers/BasePage";
import { FindifyFooterLogo, BottomNavigationPadding } from "../../genericPages/components/FindifyNavigation";
import { FindifyAppHeader } from "../../genericPages/components/FindifyAppHeader";
import { PlaceColors } from "../../genericPages/helpers/PlaceColors";
import { PlaceText } from "../../genericPages/components/PlaceText";
import { getApiFromModule } from "../../genericPages/helpers/GetContentsByModule";
import { getTranslatedString } from "../../genericPages/helpers/FindifyTranslatedString";
import { useSeoTitle } from "../../genericPages/helpers/SeoTitle";

type PassportState = {
    stamps: Record<string, string>;
    badges: Record<string, string>;
}

const PASSPORT_STORAGE_KEY = "rmcDigitalPassport";

const isEnglishLanguage = (language?: string) => language?.startsWith("en");
const localizedText = (findifyDataContext: FindifyDataContextType | null, pt: string, en: string) => (
    isEnglishLanguage(findifyDataContext?.language) ? en : pt
);

const normalizeSlug = (value: any) => String(value ?? "").trim().toLocaleLowerCase();

const getContentSlug = (content: any) => normalizeSlug(content?.page_slug ?? content?.slug ?? content?.content_id);
const getQuizImage = (quiz: any) => {
    const media = quiz?.featured_image_media;
    if (typeof media === "string") return media;
    return media?.file_url ?? media?.file_url_path ?? media?.url ?? media?.path ?? "";
};

const isMuseumContent = (content: any) => {
    return content?.categories?.some((line: any) => {
        const category = line?.category ?? line;
        const slug = normalizeSlug(category?.page_slug ?? category?.slug ?? line?.page_slug ?? line?.slug);
        const name = normalizeSlug(getTranslatedString(category?.name ?? line?.name, { language: "pt-PT" } as FindifyDataContextType));
        return slug === "museus" || name === "museus";
    });
}

const loadPassport = async (): Promise<PassportState> => {
    const raw = await EncryptedStorage.getItem(PASSPORT_STORAGE_KEY);
    if (!raw) return { stamps: {}, badges: {} };
    try {
        const parsed = JSON.parse(raw);
        return {
            stamps: parsed?.stamps ?? {},
            badges: parsed?.badges ?? {},
        };
    } catch {
        return { stamps: {}, badges: {} };
    }
}

const savePassport = (state: PassportState) => EncryptedStorage.setItem(PASSPORT_STORAGE_KEY, JSON.stringify(state));

export const PassportPage = ({ navigation }: any) => {
    const findifyDataContext = useContext(FindifyDataContext);
    const findifyMenuContext = useContext(FindifyMenuContext);
    const colors = PlaceColors(findifyDataContext);
    const [passport, setPassport] = useState<PassportState>({ stamps: {}, badges: {} });
    const [museums, setMuseums] = useState<any[]>([]);
    const [quizzes, setQuizzes] = useState<any[]>([]);
    const [quizzesLoading, setQuizzesLoading] = useState(true);
    const activeMuseumSlug = normalizeSlug(findifyMenuContext?.activeMuseumSlug);
    useSeoTitle(localizedText(findifyDataContext, "Passaporte Digital", "Digital Passport"), "Rota de Museus de Caminha");

    useEffect(() => {
        loadPassport().then(setPassport);
    }, []);

    useEffect(() => {
        const api = getApiFromModule("contents", findifyDataContext as FindifyDataContextType);
        api?.getList(0, 100, null, {}, null)
            .then((res: any) => {
                setMuseums((res?.results ?? []).filter(isMuseumContent));
            })
            .catch(() => setMuseums([]));
    }, [findifyDataContext?.workspace?.workspace_id]);

    useEffect(() => {
        let active = true;
        const api: any = getApiFromModule("quizzes", findifyDataContext as FindifyDataContextType);
        setQuizzesLoading(true);
        api?.listPublic(findifyDataContext?.workspace?.workspace_id, findifyDataContext?.language ?? "pt-PT")
            .then((res: any) => {
                const items = (Array.isArray(res?.results ?? res?.data ?? res?.quizzes ?? res) ? (res?.results ?? res?.data ?? res?.quizzes ?? res) : [res]).filter((item: any) => item?.slug ?? item?.page_slug);
                const loadDetails = async () => Promise.all(items.map(async (item: any) => {
                    if (getQuizImage(item)) return item;
                    try {
                        const detail = await api.getPublic(item.slug ?? item.page_slug, findifyDataContext?.language ?? "pt-PT");
                        return { ...item, ...detail };
                    } catch { return item; }
                }));
                loadDetails().then((result) => active && setQuizzes(result));
            })
            .catch(() => active && setQuizzes([]))
            .finally(() => active && setQuizzesLoading(false));
        return () => { active = false; };
    }, [findifyDataContext?.workspace?.workspace_id, findifyDataContext?.language]);

    const updatePassport = (updater: (current: PassportState) => PassportState) => {
        setPassport((current) => {
            const next = updater(current);
            savePassport(next).catch((error) => console.error("Failed to save passport:", error));
            return next;
        });
    }

    useEffect(() => {
        if (!activeMuseumSlug) return;
        updatePassport((current) => {
            if (current.stamps[activeMuseumSlug]) return current;
            return {
                ...current,
                stamps: {
                    ...current.stamps,
                    [activeMuseumSlug]: new Date().toISOString(),
                },
            };
        });
    }, [activeMuseumSlug]);

    const stampCount = Object.keys(passport.stamps).length;
    const badgeCount = Object.keys(passport.badges).length;
    const totalMuseums = Math.max(museums.length, stampCount);
    const progress = totalMuseums > 0 ? Math.min(100, Math.round((stampCount / totalMuseums) * 100)) : 0;
    const activeMuseum = museums.find((museum: any) => getContentSlug(museum) === activeMuseumSlug);

    return <BasePage navigation={navigation}>
        <View style={[styles.page, { backgroundColor: colors.background.get() }]}>
            <FindifyAppHeader findifyDataContext={findifyDataContext} navigation={navigation} />
            <ScrollView
                bounces={false}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={styles.content}>
                <View style={[styles.hero, { backgroundColor: colors.primary.get() }]}>
                    <Icon name="ticket-outline" size={42} color={colors.secondary.get()} />
                    <PlaceText style={styles.heroTitle}>{localizedText(findifyDataContext, "Passaporte Digital", "Digital Passport")}</PlaceText>
                    <PlaceText style={styles.heroCopy}>
                        {localizedText(findifyDataContext, "Colecione carimbos ao visitar núcleos ou ler QR Codes e complete a Rota de Museus de Caminha.", "Collect stamps by visiting museums or scanning QR Codes and complete the Caminha Museum Route.")}
                    </PlaceText>
                    <View style={styles.progressWrap}>
                        <View style={[styles.progressTrack, { backgroundColor: colors.background.withAlpha(80) }]}>
                            <View style={[styles.progressBar, { width: `${progress}%`, backgroundColor: colors.secondary.get() }]} />
                        </View>
                        <PlaceText style={styles.progressText}>{stampCount}/{totalMuseums || 0} {localizedText(findifyDataContext, "núcleos", "museums")}</PlaceText>
                    </View>
                </View>

                <View style={styles.statsRow}>
                    <View style={[styles.statCard, { borderColor: colors.primary.withAlpha(35) }]}>
                        <Icon name="seal-outline" size={26} color={colors.primary.get()} />
                        <PlaceText style={[styles.statNumber, { color: colors.primary.get() }]}>{stampCount}</PlaceText>
                        <PlaceText style={[styles.statLabel, { color: colors.text.withAlpha(190) }]}>{localizedText(findifyDataContext, "Carimbos", "Stamps")}</PlaceText>
                    </View>
                    <View style={[styles.statCard, { borderColor: colors.primary.withAlpha(35) }]}>
                        <Icon name="medal-outline" size={26} color={colors.primary.get()} />
                        <PlaceText style={[styles.statNumber, { color: colors.primary.get() }]}>{badgeCount}</PlaceText>
                        <PlaceText style={[styles.statLabel, { color: colors.text.withAlpha(190) }]}>{localizedText(findifyDataContext, "Emblemas", "Badges")}</PlaceText>
                    </View>
                </View>

                {activeMuseumSlug ? (
                    <View style={[styles.activeStampCard, { backgroundColor: colors.secondary.withAlpha(32), borderColor: colors.secondary.get() }]}>
                        <Icon name="checkmark-circle" size={28} color={colors.primary.get()} />
                        <View style={{ flex: 1 }}>
                            <PlaceText style={[styles.activeStampTitle, { color: colors.primary.get() }]}>
                                {localizedText(findifyDataContext, "Carimbo registado", "Stamp registered")}
                            </PlaceText>
                            <PlaceText style={[styles.activeStampCopy, { color: colors.text.get() }]}>
                                {getTranslatedString(activeMuseum?.name, findifyDataContext as FindifyDataContextType) || activeMuseumSlug}
                            </PlaceText>
                        </View>
                    </View>
                ) : null}

                <View style={styles.sectionHeader}>
                    <PlaceText style={[styles.sectionTitle, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, "Coleção de núcleos", "Museum collection")}</PlaceText>
                    <PlaceText style={[styles.sectionCopy, { color: colors.text.withAlpha(185) }]}>{localizedText(findifyDataContext, "Cada núcleo visitado adiciona um carimbo ao passaporte.", "Each visited museum adds a stamp to your passport.")}</PlaceText>
                </View>

                <View style={styles.stampsGrid}>
                    {museums.map((museum: any) => {
                        const slug = getContentSlug(museum);
                        const stamped = !!passport.stamps[slug];
                        return <View key={`passport_stamp_${slug}`} style={[styles.stampCard, { borderColor: stamped ? colors.secondary.get() : colors.primary.withAlpha(28), backgroundColor: stamped ? colors.secondary.withAlpha(22) : colors.background.solidAlphaBlackBase(8) }]}>
                            <View style={[styles.stampIcon, { backgroundColor: stamped ? colors.primary.get() : colors.primary.withAlpha(35) }]}>
                                <Icon name={stamped ? "checkmark" : "ellipse-outline"} size={24} color={stamped ? "#fff" : colors.primary.get()} />
                            </View>
                            <PlaceText numberOfLines={2} style={[styles.stampTitle, { color: colors.text.get() }]}>{getTranslatedString(museum?.name, findifyDataContext as FindifyDataContextType)}</PlaceText>
                        </View>
                    })}
                </View>

                <View style={styles.sectionHeader}>
                    <PlaceText style={[styles.sectionTitle, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, "Quizzes e emblemas", "Quizzes and badges")}</PlaceText>
                    <PlaceText style={[styles.sectionCopy, { color: colors.text.withAlpha(185) }]}>{localizedText(findifyDataContext, "Complete pequenos desafios para desbloquear emblemas.", "Complete quick challenges to unlock badges.")}</PlaceText>
                </View>

                <View style={styles.badgesList}>
                    {quizzesLoading ? <ActivityIndicator color={colors.primary.get()} /> : quizzes.length ? quizzes.map((quiz: any) => {
                        const quizSlug = normalizeSlug(quiz?.slug ?? quiz?.page_slug);
                        const earned = !!passport.badges[quizSlug];
                        return <View key={`quiz_${quiz.id}`} style={[styles.badgeCard, { borderColor: earned ? colors.secondary.get() : colors.primary.withAlpha(28) }]}>
                            <View style={[styles.badgeIcon, { backgroundColor: earned ? colors.secondary.get() : colors.primary.withAlpha(30) }]}>
                                {getQuizImage(quiz) ? <Image source={{ uri: getQuizImage(quiz) }} style={styles.badgeImage} /> : <Icon name={earned ? "medal" : "help-circle-outline"} size={28} color={colors.primary.get()} />}
                            </View>
                            <View style={{ flex: 1 }}>
                                <PlaceText style={[styles.badgeTitle, { color: colors.primary.get() }]}>{getTranslatedString(quiz?.title, findifyDataContext as FindifyDataContextType) || localizedText(findifyDataContext, "Quiz", "Quiz")}</PlaceText>
                                <PlaceText style={[styles.badgeCopy, { color: colors.text.withAlpha(190) }]}>{getTranslatedString(quiz?.description || quiz?.resume, findifyDataContext as FindifyDataContextType) || localizedText(findifyDataContext, "Complete este quiz para desbloquear um emblema.", "Complete this quiz to unlock a badge.")}</PlaceText>
                            </View>
                            <Pressable
                                disabled={earned}
                                style={[styles.quizButton, { backgroundColor: earned ? colors.secondary.withAlpha(50) : colors.primary.get() }]}
                                onPress={() => navigation.push('RmcQuiz', { slug: quizSlug })}>
                                <PlaceText style={styles.quizButtonText}>{earned ? localizedText(findifyDataContext, "Obtido", "Earned") : localizedText(findifyDataContext, "Quiz", "Quiz")}</PlaceText>
                            </Pressable>
                        </View>
                    }) : <PlaceText style={[styles.sectionCopy, { color: colors.text.withAlpha(185) }]}>{localizedText(findifyDataContext, "Ainda não existem quizzes disponíveis.", "No quizzes are currently available.")}</PlaceText>}
                </View>

                <FindifyFooterLogo findifyDataContext={findifyDataContext} />
            </ScrollView>
        </View>
    </BasePage>
}

const styles = StyleSheet.create({
    page: {
        flex: 1,
    },
    content: {
        padding: 22,
        paddingBottom: BottomNavigationPadding + 48,
        gap: 18,
    },
    hero: {
        borderRadius: 8,
        padding: 22,
        gap: 12,
    },
    heroTitle: {
        color: "#fff",
        fontSize: 30,
        lineHeight: 36,
        fontFamily: "Georgia",
        fontWeight: "700",
    },
    heroCopy: {
        color: "rgba(255,255,255,0.88)",
        fontSize: 16,
        lineHeight: 24,
    },
    progressWrap: {
        gap: 8,
        marginTop: 8,
    },
    progressTrack: {
        height: 10,
        borderRadius: 8,
        overflow: "hidden",
    },
    progressBar: {
        height: "100%",
        borderRadius: 8,
    },
    progressText: {
        color: "#fff",
        fontSize: 13,
        fontWeight: "700",
    },
    statsRow: {
        flexDirection: "row",
        gap: 12,
    },
    statCard: {
        flex: 1,
        borderWidth: 1,
        borderRadius: 8,
        padding: 16,
        alignItems: "center",
        gap: 5,
    },
    statNumber: {
        fontSize: 28,
        fontWeight: "800",
    },
    statLabel: {
        fontSize: 13,
        fontWeight: "700",
    },
    activeStampCard: {
        borderWidth: 1,
        borderRadius: 8,
        padding: 14,
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
    },
    activeStampTitle: {
        fontSize: 16,
        fontWeight: "800",
    },
    activeStampCopy: {
        fontSize: 14,
        marginTop: 2,
    },
    sectionHeader: {
        gap: 4,
        marginTop: 8,
    },
    sectionTitle: {
        fontSize: 22,
        lineHeight: 28,
        fontFamily: "Georgia",
        fontWeight: "700",
    },
    sectionCopy: {
        fontSize: 14,
        lineHeight: 21,
    },
    stampsGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 10,
    },
    stampCard: {
        width: "48%" as any,
        minHeight: 128,
        borderWidth: 1,
        borderRadius: 8,
        padding: 12,
        justifyContent: "space-between",
    },
    stampIcon: {
        width: 42,
        height: 42,
        borderRadius: 21,
        alignItems: "center",
        justifyContent: "center",
    },
    stampTitle: {
        fontSize: 15,
        lineHeight: 20,
        fontWeight: "700",
    },
    badgesList: {
        gap: 10,
    },
    badgeCard: {
        borderWidth: 1,
        borderRadius: 8,
        padding: 12,
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
    },
    badgeIcon: {
        width: 48,
        height: 48,
        borderRadius: 24,
        alignItems: "center",
        justifyContent: "center",
    },
    badgeImage: {
        width: "100%",
        height: "100%",
        borderRadius: 24,
    },
    badgeTitle: {
        fontSize: 16,
        fontWeight: "800",
    },
    badgeCopy: {
        fontSize: 13,
        lineHeight: 18,
        marginTop: 2,
    },
    quizButton: {
        minWidth: 72,
        height: 40,
        borderRadius: 8,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 12,
    },
    quizButtonText: {
        color: "#fff",
        fontWeight: "800",
        fontSize: 13,
    },
});
